import type {
  ChangePayrollModelInput,
  ChangeUserRoleInput,
  CreateEmployeeInput,
  EmployeeResponse,
  EmployeeStatus,
  CountryCode,
  ProvisionableUserRole,
  ProvisionEmployeeUserInput,
  TerminateEmployeeInput,
  TerminateEmployeeResult,
  UpdateEmployeeInput,
  UserRole,
} from '@kora/types';
import { apiRequest } from './client.js';

/** Response shape from POST /v1/employees/:id/provision-user. */
export interface ProvisionEmployeeUserResult {
  userId: string;
  email: string;
  role: ProvisionableUserRole;
  status: 'invited' | 'active' | 'disabled';
  employeeId: string;
  employeeNumber: string;
  supersededPriorInvitation: boolean;
}

/** Response shape from PATCH /v1/employees/:id/user/role. */
export interface ChangeUserRoleResult {
  userId: string;
  email: string;
  role: UserRole;
  previousRole: UserRole;
  employeeId: string;
}

/**
 * Employees API. Thin typed wrappers around the REST endpoints in
 * apps/api/src/routes/employees.ts. The server's Zod schemas live in
 * @kora/types — we import the inferred types here for compile-time
 * contract-matching between FE and BE.
 */

// ---------------------------------------------------------------------------
// GET /v1/employees — list with keyset pagination
// ---------------------------------------------------------------------------

export interface ListEmployeesArgs {
  status?: EmployeeStatus;
  countryCode?: CountryCode;
  cursor?: string;
  limit?: number;
}

export interface EmployeeListPage {
  employees: EmployeeResponse[];
  nextCursor: string | null;
}

export function listEmployees(args: ListEmployeesArgs = {}): Promise<EmployeeListPage> {
  const params = new URLSearchParams();
  if (args.status) params.set('status', args.status);
  if (args.countryCode) params.set('countryCode', args.countryCode);
  if (args.cursor) params.set('cursor', args.cursor);
  if (args.limit) params.set('limit', String(args.limit));
  const qs = params.toString();
  return apiRequest<EmployeeListPage>(`/v1/employees${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// GET /v1/employees/:id
// ---------------------------------------------------------------------------

export function getEmployee(id: string): Promise<EmployeeResponse> {
  return apiRequest<EmployeeResponse>(`/v1/employees/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// POST /v1/employees
// ---------------------------------------------------------------------------

export function createEmployee(input: CreateEmployeeInput): Promise<EmployeeResponse> {
  return apiRequest<EmployeeResponse>('/v1/employees', {
    method: 'POST',
    body: input,
  });
}

// ---------------------------------------------------------------------------
// PATCH /v1/employees/:id — partial update of mutable fields
// ---------------------------------------------------------------------------
//
// Mutable: firstName, lastName, email, phone, stateCode, taxIdentifier,
// socialSecurityId, dateOfBirth, homeAddress, nextOfKin, status (active
// ↔ on_leave only — terminate uses its own endpoint).
//
// IMMUTABLE (deliberately rejected by the schema): employeeNumber,
// countryCode, hireDate. These are load-bearing for payroll history;
// changing them would silently rewrite past calculations. To "rehire"
// or move countries, create a new employee record.

export function patchEmployee(
  id: string,
  input: UpdateEmployeeInput,
): Promise<EmployeeResponse> {
  return apiRequest<EmployeeResponse>(`/v1/employees/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
  });
}

// ---------------------------------------------------------------------------
// POST /v1/employees/:id/terminate
// ---------------------------------------------------------------------------

export function terminateEmployee(
  id: string,
  input: TerminateEmployeeInput,
): Promise<TerminateEmployeeResult> {
  return apiRequest<TerminateEmployeeResult>(
    `/v1/employees/${encodeURIComponent(id)}/terminate`,
    { method: 'POST', body: input },
  );
}

// ---------------------------------------------------------------------------
// POST /v1/employees/:id/provision-user
// ---------------------------------------------------------------------------

/**
 * Owner/admin/HR provisions a login for an existing employee. The server
 * creates the user in `invited` status and emails them a one-time link to
 * set their own password — no password is set here.
 *
 * Throws ApiError on:
 *   - 400 no email supplied and the employee has none on file
 *   - 404 employee not found
 *   - 409 employee already has a login (re-provisioning is a delete + recreate)
 *   - 409 email taken by another user in the org
 *   - 409 employee is terminated
 */
export function provisionEmployeeUser(
  id: string,
  input: ProvisionEmployeeUserInput,
): Promise<ProvisionEmployeeUserResult> {
  return apiRequest<ProvisionEmployeeUserResult>(
    `/v1/employees/${encodeURIComponent(id)}/provision-user`,
    { method: 'POST', body: input },
  );
}

// ---------------------------------------------------------------------------
// PATCH /v1/employees/:id/user/role
// ---------------------------------------------------------------------------

/**
 * Owner/admin changes the role of the user linked to an employee. The server
 * emails the affected user a notification.
 *
 * Throws ApiError on:
 *   - 400 changing your own role, demoting the sole owner, or a no-op change
 *   - 403 caller is not owner/admin
 *   - 404 employee has no linked user
 */
export function changeUserRole(
  id: string,
  input: ChangeUserRoleInput,
): Promise<ChangeUserRoleResult> {
  return apiRequest<ChangeUserRoleResult>(
    `/v1/employees/${encodeURIComponent(id)}/user/role`,
    { method: 'PATCH', body: input },
  );
}

/**
 * ADR 0010 Phase 4 — owner/admin changes an employee's payroll model.
 * Throws ApiError; the caller inspects 409 + body.warningCode ===
 * 'payroll_model_misclassification' to drive the soft-warning confirmation flow,
 * re-submitting with `confirmMisclassificationWarning: true`.
 */
export function changePayrollModel(
  id: string,
  input: ChangePayrollModelInput,
): Promise<EmployeeResponse> {
  return apiRequest<EmployeeResponse>(
    `/v1/employees/${encodeURIComponent(id)}/payroll-model`,
    { method: 'PATCH', body: input },
  );
}
